import { useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import clsx from "clsx";
import { formatPrice, formatDate, formatPercent } from "@/lib/format";
import type { TradeRow } from "@/hooks/useTrades";

interface TradeHistoryProps {
  trades: TradeRow[];
  onDelete: (id: string) => void;
}

export function TradeHistory({ trades, onDelete }: TradeHistoryProps) {
  const [expanded, setExpanded] = useState(false);

  const closed = trades.filter((t) => t.result !== "open");
  const open = trades.filter((t) => t.result === "open");

  return (
    <div className="shrink-0 border-t border-border bg-bg-panel">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex h-8 w-full items-center justify-between border-b border-border px-4 text-2xs uppercase tracking-wider text-text-secondary hover:bg-bg-elevated"
      >
        <div className="flex items-center gap-3">
          <span>Trade history</span>
          <span className="text-text-muted normal-case">
            {closed.length} closed · {open.length} open
          </span>
        </div>
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
      </button>

      {expanded && (
        <div className="max-h-48 overflow-y-auto">
          {trades.length === 0 ? (
            <div className="px-4 py-6 text-center text-2xs text-text-muted">
              No trades yet. Press B (buy) or S (sell) at any candle to start.
            </div>
          ) : (
            <table className="w-full text-2xs tabular font-mono">
              <thead className="sticky top-0 bg-bg-panel text-text-muted">
                <tr className="border-b border-border">
                  <Th>Entry</Th>
                  <Th>Symbol</Th>
                  <Th>Dir</Th>
                  <Th align="right">Qty</Th>
                  <Th align="right">Entry $</Th>
                  <Th align="right">Exit $</Th>
                  <Th align="right">P&amp;L</Th>
                  <Th align="right">%</Th>
                  <Th>Result</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <Row key={t.id} trade={t} onDelete={() => onDelete(t.id)} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }: { children?: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      className={clsx(
        "px-2 py-1.5 font-semibold uppercase tracking-wider",
        align === "right" ? "text-right" : "text-left"
      )}
    >
      {children}
    </th>
  );
}

function Row({ trade, onDelete }: { trade: TradeRow; onDelete: () => void }) {
  const pnl = trade.pnl;
  const pnlPct =
    trade.exit_price !== null && trade.entry_price !== 0
      ? ((trade.exit_price - trade.entry_price) / trade.entry_price) *
        100 *
        (trade.direction === "long" ? 1 : -1)
      : null;

  const pnlClass = pnl === null ? "text-text-muted" : pnl > 0 ? "text-bull" : pnl < 0 ? "text-bear" : "text-text-secondary";

  const resultPillClass = {
    open:      "bg-accent/15 text-accent",
    win:       "bg-bull/15 text-bull",
    loss:      "bg-bear/15 text-bear",
    breakeven: "bg-text-muted/15 text-text-secondary",
    manual:    "bg-warn/15 text-warn",
  }[trade.result];

  return (
    <tr className="group border-b border-border/50 hover:bg-bg-elevated">
      <Td>{formatDate(trade.entry_ts)}</Td>
      <Td className="text-text-primary">{trade.symbol}</Td>
      <Td>
        <span className={trade.direction === "long" ? "text-bull" : "text-bear"}>
          {trade.direction.toUpperCase()}
        </span>
      </Td>
      <Td align="right">{trade.quantity}</Td>
      <Td align="right">{formatPrice(trade.entry_price)}</Td>
      <Td align="right">{trade.exit_price !== null ? formatPrice(trade.exit_price) : "—"}</Td>
      <Td align="right" className={pnlClass}>
        {pnl === null ? "—" : `${pnl >= 0 ? "+" : ""}${formatPrice(pnl)}`}
      </Td>
      <Td align="right" className={pnlClass}>
        {pnlPct === null ? "—" : formatPercent(pnlPct)}
      </Td>
      <Td>
        <span className={clsx("rounded px-1.5 py-0.5 uppercase tracking-wider text-[10px]", resultPillClass)}>
          {trade.result}
        </span>
      </Td>
      <Td>
        <button
          onClick={onDelete}
          className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-bear transition-opacity"
          title="Delete trade"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </Td>
    </tr>
  );
}

function Td({
  children,
  align = "left",
  className,
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <td
      className={clsx(
        "px-2 py-1.5 text-text-secondary",
        align === "right" ? "text-right" : "text-left",
        className
      )}
    >
      {children}
    </td>
  );
}
